import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Flame, MessageCircle, BookHeart, Waves, User, Settings as SettingsIcon, Plus, LogOut, Menu, Sparkles } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import DailyCheckIn from "@/components/DailyCheckIn";
import { toast } from "sonner";

function SidebarContent({ onNewConversation, conversations, onNavigate, user, onLogout }) {
    return (
        <div className="h-full w-full flex flex-col bg-brand-darkPurple text-white">
            {/* Brand */}
            <div className="px-6 pt-8 pb-5">
                <div className="flex items-center gap-2.5">
                    <Flame className="w-5 h-5 text-brand-goldLight animate-flicker" strokeWidth={1.5} />
                    <span className="font-serif text-2xl tracking-tight">Lantern</span>
                </div>
                <p className="text-white/50 text-xs mt-1 font-light">a companion for the turn</p>
            </div>

            {/* New conversation */}
            <div className="px-4 pb-3">
                <button
                    data-testid="sidebar-new-conversation-btn"
                    onClick={onNewConversation}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-brand-goldLight/50 transition-colors duration-200 text-sm"
                >
                    <Plus className="w-4 h-4 text-brand-goldLight" strokeWidth={1.75} />
                    <span>New conversation</span>
                </button>
            </div>

            {/* Conversations list */}
            <div className="flex-1 overflow-y-auto sidebar-scroll px-2 pb-3">
                <div className="px-4 py-2 text-[11px] uppercase tracking-widest text-white/40">Conversations</div>
                {conversations.length === 0 ? (
                    <div className="px-4 py-3 text-white/40 text-sm font-light italic">Nothing yet — a soft, empty page.</div>
                ) : (
                    <ul className="space-y-0.5">
                        {conversations.map((c) => (
                            <li key={c.conversation_id}>
                                <NavLink
                                    data-testid={`sidebar-conv-${c.conversation_id}`}
                                    to={`/chat/${c.conversation_id}`}
                                    onClick={onNavigate}
                                    className={({ isActive }) =>
                                        `block px-4 py-2 rounded-lg text-sm transition-colors ${
                                            isActive ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                                        }`
                                    }
                                >
                                    <span className="truncate block">{c.title || "New conversation"}</span>
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Bottom nav */}
            <div className="border-t border-white/10 px-2 py-3">
                <NavLink
                    data-testid="sidebar-insights-link"
                    to="/insights"
                    onClick={onNavigate}
                    className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/75 hover:bg-white/5"}`}
                >
                    <BookHeart className="w-4 h-4 text-brand-goldLight" strokeWidth={1.5} />
                    Saved insights
                </NavLink>
                <NavLink
                    data-testid="sidebar-patterns-link"
                    to="/patterns"
                    onClick={onNavigate}
                    className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/75 hover:bg-white/5"}`}
                >
                    <Waves className="w-4 h-4 text-brand-goldLight" strokeWidth={1.5} />
                    Patterns
                </NavLink>
                <NavLink
                    data-testid="sidebar-dashboard-link"
                    to="/dashboard"
                    onClick={onNavigate}
                    className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/75 hover:bg-white/5"}`}
                >
                    <Sparkles className="w-4 h-4 text-brand-goldLight" strokeWidth={1.5} />
                    Today
                </NavLink>
            </div>

            {/* User */}
            <div className="border-t border-white/10 px-4 py-4">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-goldLight/20 flex items-center justify-center text-brand-goldLight font-serif text-lg">
                        {user?.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{user?.name}</div>
                        <div className="text-xs text-white/50 truncate">{user?.email}</div>
                    </div>
                </div>
                <div className="mt-3 flex gap-1.5">
                    <NavLink
                        data-testid="sidebar-profile-link"
                        to="/profile"
                        onClick={onNavigate}
                        className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-white/70 hover:bg-white/5 hover:text-white transition-colors"
                    >
                        <User className="w-3.5 h-3.5" strokeWidth={1.75} /> Profile
                    </NavLink>
                    <NavLink
                        data-testid="sidebar-settings-link"
                        to="/settings"
                        onClick={onNavigate}
                        className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-white/70 hover:bg-white/5 hover:text-white transition-colors"
                    >
                        <SettingsIcon className="w-3.5 h-3.5" strokeWidth={1.75} /> Settings
                    </NavLink>
                    <button
                        data-testid="sidebar-logout-btn"
                        onClick={onLogout}
                        className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs text-white/70 hover:bg-white/5 hover:text-white transition-colors"
                        aria-label="Log out"
                    >
                        <LogOut className="w-3.5 h-3.5" strokeWidth={1.75} />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AppLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [conversations, setConversations] = useState([]);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [checkInOpen, setCheckInOpen] = useState(false);

    const loadConversations = useCallback(async () => {
        try {
            const res = await api.get("/conversations");
            setConversations(res.data || []);
        } catch (e) { /* ignore */ }
    }, []);

    useEffect(() => { loadConversations(); }, [loadConversations, location.pathname]);

    const handleNewConversation = async () => {
        try {
            const res = await api.post("/conversations", { mode: "open" });
            await loadConversations();
            setMobileOpen(false);
            navigate(`/chat/${res.data.conversation_id}`);
        } catch (e) {
            toast.error("Could not start a new conversation.");
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate("/", { replace: true });
    };

    return (
        <div className="min-h-screen flex bg-brand-cream cream-grain" data-testid="app-layout">
            {/* Desktop sidebar */}
            <aside className="hidden md:flex md:w-72 lg:w-80 shrink-0 sticky top-0 h-screen">
                <SidebarContent
                    onNewConversation={handleNewConversation}
                    conversations={conversations}
                    onNavigate={() => {}}
                    user={user}
                    onLogout={handleLogout}
                />
            </aside>

            {/* Mobile drawer */}
            <div className="md:hidden fixed top-3 left-3 z-40">
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                    <SheetTrigger asChild>
                        <Button
                            data-testid="mobile-menu-btn"
                            variant="ghost"
                            size="icon"
                            className="bg-brand-darkPurple text-white hover:bg-brand-darkPurpleSoft rounded-full h-10 w-10 shadow-soft-card"
                        >
                            <Menu className="w-4 h-4" />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="p-0 w-[85vw] max-w-sm border-none bg-brand-darkPurple">
                        <SidebarContent
                            onNewConversation={handleNewConversation}
                            conversations={conversations}
                            onNavigate={() => setMobileOpen(false)}
                            user={user}
                            onLogout={handleLogout}
                        />
                    </SheetContent>
                </Sheet>
            </div>

            {/* Main */}
            <main className="flex-1 min-w-0 flex flex-col">
                <Outlet context={{ onCheckIn: () => setCheckInOpen(true), refreshConversations: loadConversations }} />
            </main>

            <DailyCheckIn open={checkInOpen} onOpenChange={setCheckInOpen} />
        </div>
    );
}
